import { fetcher } from '../../../components/Hooks/useListActions';
import { GetProductDetailsProductColor } from '../../../modules/orders/products/interfaces/get-order-product-grid.interface';
import { ESProductSearch } from '../../../modules/products/ProductTypes';
import { ESProduct } from '../../../modules/products/product.types.v2';
import {
  GetProductDetailsEmptyAvailabilitiesOutput,
  ProductDetailsDTO,
  ProductDetailsGroupSection,
} from '../../../modules/products/products-details-v2/types/productDetails.types';
import { restApi } from '../../api';
import {
  BatchPasteGridParams,
  CombinationKey,
  IProductDetailsCombinationsParams,
  IProductDetailsFilter,
  IProductDetailsFilterParams,
  IProductDetailsGroupSectionParams,
  ProductDetailsFiltersParams,
  ProductDetailsMedias,
  ProductDetailsMediasParams,
  ProductDetailsSize,
  ProductDetailsSummary,
  ValidateCombinationsDTO,
} from './productDetails.types';

const encodePair = (key: string, value: unknown): string =>
  `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object';

const serializeArrayItem = (key: string, item: unknown, index: number): string[] => {
  if (isObject(item)) {
    return Object.entries(item)
      .filter(([, subValue]) => subValue !== undefined)
      .map(([subKey, subValue]) => encodePair(`${key}[${index}][${subKey}]`, subValue));
  }

  if (item === undefined) {
    return [];
  }

  return [encodePair(`${key}[${index}]`, item)];
};

const serializeEntry = (key: string, value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => serializeArrayItem(key, item, index));
  }

  if (value === undefined || value === null) {
    return [];
  }

  return [encodePair(key, value)];
};

const serializeWithIndices = (params: Record<string, unknown>): string =>
  Object.entries(params)
    .flatMap(([key, value]) => serializeEntry(key, value))
    .join('&');

const PATH = 'products/details';

const onlineProductDetailsRepository = () => ({
  getProductDetailsProductColor: ({
    productColorId,
    isAdminSeller,
    orderId,
  }: GetProductDetailsProductColor) =>
    restApi.get<ESProductSearch>(`catalog-product-colors/${productColorId}`, {
      isAdminSeller,
      orderId,
    }),

  getProductDetailsCombinations: (params: IProductDetailsCombinationsParams) =>
    restApi.get<ProductDetailsDTO[]>(`${PATH}/combinations`, params),

  getProductDetailsGroupSections: (params: IProductDetailsGroupSectionParams) =>
    restApi.get<ProductDetailsGroupSection[]>(`${PATH}/count`, params),

  getProductDetailsEmptyAvailabilities: (params: IProductDetailsGroupSectionParams) =>
    restApi.get<GetProductDetailsEmptyAvailabilitiesOutput[]>(
      `${PATH}/empty-availabilities`,
      params,
    ),

  getProductDetailsFilter: (params: IProductDetailsFilterParams) =>
    restApi.get<IProductDetailsFilter>(`${PATH}/filter`, params),

  getProductDetailsMedias: (params: ProductDetailsMediasParams) =>
    restApi.get<ProductDetailsMedias>(`${PATH}/medias`, params),

  getProductDetailsSizes: (params: ProductDetailsFiltersParams) =>
    restApi.get<ProductDetailsSize[]>(`${PATH}/sizes`, params),

  getProductDetailsSummary: (params: IProductDetailsGroupSectionParams) =>
    restApi.get<ProductDetailsSummary>(`${PATH}/summary`, params),

  getSuggestedProducts: (productColorId: string) =>
    fetcher<ESProduct, ESProduct>(`/product-colors/${productColorId}/suggested-products`),

  postBatchPasteGrid: (params: BatchPasteGridParams) =>
    restApi.post<void>(
      `${PATH}/batch-paste-grid`,
      {
        copiedGrid: params.copiedGrid,
        isAdminSeller: params.isAdminSeller,
        combinations: params.combinations,
        pasteInAllCombinations: params.pasteInAllCombinations,
        unselectedCombinations: params.unselectedCombinations,
      },
      { params },
    ),

  getCombinationsValidation: (params: { orderIds: string[]; combinations: CombinationKey[] }) =>
    restApi.get<ValidateCombinationsDTO>(
      `${PATH}/combinations/validate`,
      {},
      {
        paramsSerializer: () =>
          serializeWithIndices({
            orderIds: params.orderIds,
            combinations: params.combinations,
          }),
      },
    ),
});

export default onlineProductDetailsRepository;
